/**
 * The simulation. Headless, deterministic, DOM-free.
 *
 * Time enters this module through exactly one door: `step(deltaMs)`. Nothing in
 * `core/` reads a clock. Piece supply enters through exactly one door: the
 * `PieceGenerator` handed to the constructor.
 */

import { Board, BOARD_HEIGHT, BOARD_WIDTH, BUFFER_HEIGHT, VISIBLE_HEIGHT } from './board.ts';
import { Emitter } from './events.ts';
import { SevenBagGenerator, type PieceGenerator } from './generator.ts';
import {
  cellsAt,
  pieceCells,
  rotateIndex,
  spawnPiece,
  tCenter,
  type ActivePiece,
  type PieceType,
  type Rotation,
  type Vec,
} from './piece.ts';
import { getKicks } from './srs.ts';
import {
  gravityIntervalMs,
  levelForLines,
  scoreClear,
  type ScoringTable,
  type TSpinKind,
} from './scoring.ts';

/**
 * Every tunable in the game. Nothing below this object hardcodes a timing or a
 * score — change a rule here, not in the logic.
 */
export const TUNING = {
  board: {
    width: BOARD_WIDTH,
    visibleHeight: VISIBLE_HEIGHT,
    bufferHeight: BUFFER_HEIGHT,
    height: BOARD_HEIGHT,
  },
  /** Delayed Auto Shift: hold time before a held direction repeats (ms). */
  dasMs: 167,
  /** Auto Repeat Rate: interval between repeats once DAS has charged (ms). */
  arrMs: 33,
  /** Soft drop runs this many times faster than the current gravity. */
  softDropFactor: 5,
  /** Floor on soft-drop interval so high levels stay simulable. */
  softDropMinIntervalMs: 1,
  /** Time on the ground before a piece locks (ms). */
  lockDelayMs: 500,
  /** How many times a move or rotation may restart the lock timer. */
  maxLockResets: 15,
  linesPerLevel: 8,
  previewCount: 5,
  /** Total duration of the line-clear pause, collapse included (ms). */
  lineClearDurationMs: 120,
  /** Leading white flash inside that window (~2 frames at 60Hz). */
  lineClearFlashMs: 33,
  /**
   * How long the level-up accent flash and its plaque last, for the render
   * layer (ms). Long enough to read the new level, not just register a blink.
   */
  levelUpFlashMs: 900,
  balance: {
    /** Broad tolerance for the first pieces, measured in board columns. */
    emptyLimit: 5,
    /** Each settled cell narrows the platform's stable centre range. */
    limitLossPerCell: 0.13,
    /** A loaded platform always retains this much correction room. */
    minimumLimit: 0.35,
    /**
     * Maximum visual rotation of the board before it topples. Was 10; at 8 the
     * swept box of the tilted stage stays on the 682 × 704 canvas.
     */
    maxTiltDegrees: 8,
  },
  /** Max simulated time consumed by a single `step` call (ms). */
  maxStepMs: 100,
  scoring: {
    lineClear: [0, 100, 300, 500, 800],
    tspinMini: 100,
    /** Not in the spec table; a full T-spin that clears nothing. */
    tspinZero: 400,
    tspinSingle: 800,
    tspinDouble: 1200,
    tspinTriple: 1600,
    backToBackMultiplier: 1.5,
    comboUnit: 50,
    softDropPerCell: 1,
    hardDropPerCell: 2,
  } as ScoringTable,
} as const;

export enum Phase {
  Ready = 'READY',
  Playing = 'PLAYING',
  Paused = 'PAUSED',
  GameOver = 'GAME_OVER',
}

export enum RuleSet {
  Standard = 'STANDARD',
  Balance = 'BALANCE',
}

export enum Action {
  Start = 'START',
  MoveLeft = 'MOVE_LEFT',
  MoveRight = 'MOVE_RIGHT',
  RotateCW = 'ROTATE_CW',
  RotateCCW = 'ROTATE_CCW',
  Rotate180 = 'ROTATE_180',
  SoftDropStart = 'SOFT_DROP_START',
  SoftDropEnd = 'SOFT_DROP_END',
  HardDrop = 'HARD_DROP',
  Hold = 'HOLD',
  Pause = 'PAUSE',
  Restart = 'RESTART',
}

export interface LineClearEvent {
  /** Board rows being cleared, top to bottom. */
  rows: number[];
  lines: number;
  tspin: TSpinKind;
  backToBack: boolean;
  combo: number;
  /** Points awarded for this placement. */
  points: number;
}

export interface LockEvent {
  piece: ActivePiece;
  cells: Vec[];
  tspin: TSpinKind;
  lines: number;
  /** True when the lock came from a hard drop. */
  hardDrop: boolean;
  /** Rows the piece swept on the way down. Zero unless `hardDrop`. */
  dropDistance: number;
}

export interface LevelUpEvent {
  level: number;
  previousLevel: number;
}

export interface GameOverEvent {
  reason: 'blockout' | 'topple';
  score: number;
  lines: number;
  level: number;
}

export interface GameEvents extends Record<string, unknown> {
  onLineClear: LineClearEvent;
  onLock: LockEvent;
  onLevelUp: LevelUpEvent;
  onGameOver: GameOverEvent;
  onHold: { held: PieceType; swappedIn: PieceType };
  onPhaseChange: { from: Phase; to: Phase };
}

/** Visual state of an in-progress line clear. Advanced by `step`. */
export interface ClearAnimation {
  rows: number[];
  elapsedMs: number;
  durationMs: number;
}

export interface GameStats {
  piecesPlaced: number;
  singles: number;
  doubles: number;
  triples: number;
  tetrises: number;
  tspins: number;
  maxCombo: number;
  /** Total simulated time spent in PLAYING (ms). */
  elapsedMs: number;
}

export interface GameOptions {
  generator?: PieceGenerator;
  /** Starting level. Gravity and scoring scale from here. */
  startLevel?: number;
  rules?: RuleSet;
}

export interface BalanceState {
  /** Number of settled cells contributing weight. */
  mass: number;
  /** Horizontal centre of mass in board coordinates; the pivot is at 5. */
  centerX: number;
  /** Signed distance from the pivot. Negative is left, positive is right. */
  offset: number;
  /** Maximum safe absolute offset at the current mass. */
  limit: number;
  /** Absolute offset divided by the limit. Values over 1 topple. */
  risk: number;
}

export class GameState {
  readonly events = new Emitter<GameEvents>();
  readonly board = new Board(TUNING.board.width, TUNING.board.height);

  private generator: PieceGenerator;
  private readonly startLevel: number;

  phase: Phase = Phase.Ready;
  rules: RuleSet;
  gameOverReason: GameOverEvent['reason'] | null = null;
  active: ActivePiece | null = null;
  hold: PieceType | null = null;
  canHold = true;

  score = 0;
  lines = 0;
  level = 1;
  /** -1 = no chain. 0 = first clear of a chain. */
  combo = -1;
  backToBack = false;

  clearAnim: ClearAnimation | null = null;
  stats: GameStats = emptyStats();

  /** Milliseconds of gravity credit not yet spent on a row. */
  private gravityAcc = 0;
  private lockTimerMs = 0;
  private lockResets = 0;
  private grounded = false;
  private softDropping = false;
  /** T-spin bookkeeping: was the last successful action a rotation? */
  private lastActionWasRotation = false;
  /** Index into the kick table used by that rotation (4 = final kick). */
  private lastKickIndex = 0;

  constructor(options: GameOptions = {}) {
    this.generator = options.generator ?? new SevenBagGenerator();
    this.startLevel = Math.max(1, options.startLevel ?? 1);
    this.level = this.startLevel;
    this.rules = options.rules ?? RuleSet.Standard;
  }

  // ---------------------------------------------------------------- lifecycle

  /** Clear the board and deal a fresh piece. Leaves the game in PLAYING. */
  start(): void {
    this.board.clear();
    this.generator.reset?.();
    this.score = 0;
    this.lines = 0;
    this.level = this.startLevel;
    this.combo = -1;
    this.backToBack = false;
    this.hold = null;
    this.canHold = true;
    this.clearAnim = null;
    this.gameOverReason = null;
    this.stats = emptyStats();
    this.gravityAcc = 0;
    this.softDropping = false;
    this.setPhase(Phase.Playing);
    this.spawnNext();
  }

  restart(): void {
    this.start();
  }

  /** Replace the piece supply. Takes effect from the next spawn. */
  setGenerator(generator: PieceGenerator): void {
    this.generator = generator;
  }

  /** Select the rule set used by the next run. */
  setRuleSet(rules: RuleSet): void {
    this.rules = rules;
  }

  private setPhase(to: Phase): void {
    if (this.phase === to) return;
    const from = this.phase;
    this.phase = to;
    this.events.emit('onPhaseChange', { from, to });
  }

  // ------------------------------------------------------------------ queries

  /** Next pieces, oldest first. */
  preview(count: number = TUNING.previewCount): PieceType[] {
    return this.generator.preview(count);
  }

  /** Board row the active piece would land on, or null when there is none. */
  ghostY(): number | null {
    if (!this.active) return null;
    return this.active.y + this.board.dropDistance(pieceCells(this.active));
  }

  ghostCells(): Vec[] {
    if (!this.active) return [];
    const distance = this.board.dropDistance(pieceCells(this.active));
    return pieceCells(this.active).map((c) => ({ x: c.x, y: c.y + distance }));
  }

  activeCells(): Vec[] {
    return this.active ? pieceCells(this.active) : [];
  }

  gravityIntervalMs(): number {
    return gravityIntervalMs(this.level);
  }

  isSoftDropping(): boolean {
    return this.softDropping;
  }

  /** 0..1 progress of the lock timer, for render feedback. */
  lockProgress(): number {
    if (!this.grounded) return 0;
    return Math.min(1, this.lockTimerMs / TUNING.lockDelayMs);
  }

  /** Current platform loading, excluding rows already committed to a clear. */
  balanceState(): BalanceState {
    const clearing = new Set(this.clearAnim?.rows ?? []);
    const cells = this.board.snapshot();
    let mass = 0;
    let moment = 0;

    for (let y = 0; y < this.board.height; y++) {
      if (clearing.has(y)) continue;
      for (let x = 0; x < this.board.width; x++) {
        if (cells[y * this.board.width + x] === null) continue;
        mass++;
        moment += x + 0.5;
      }
    }

    const pivotX = this.board.width / 2;
    const centerX = mass === 0 ? pivotX : moment / mass;
    const offset = centerX - pivotX;
    const limit = Math.max(
      TUNING.balance.minimumLimit,
      TUNING.balance.emptyLimit - mass * TUNING.balance.limitLossPerCell,
    );
    return { mass, centerX, offset, limit, risk: Math.abs(offset) / limit };
  }

  // ------------------------------------------------------------------- inputs

  applyInput(action: Action): void {
    switch (this.phase) {
      case Phase.Ready:
        // Any key gets the game moving; the keypress itself is consumed.
        if (action !== Action.SoftDropEnd) this.start();
        return;

      case Phase.GameOver:
        if (action === Action.Restart || action === Action.Start) this.restart();
        return;

      case Phase.Paused:
        if (action === Action.Pause || action === Action.Start) this.setPhase(Phase.Playing);
        else if (action === Action.Restart) this.restart();
        else if (action === Action.SoftDropEnd) this.softDropping = false;
        return;

      case Phase.Playing:
        this.applyPlayingInput(action);
        return;
    }
  }

  private applyPlayingInput(action: Action): void {
    switch (action) {
      case Action.Pause:
        this.softDropping = false;
        this.setPhase(Phase.Paused);
        return;
      case Action.Restart:
        this.restart();
        return;
      case Action.SoftDropStart:
        this.softDropping = true;
        return;
      case Action.SoftDropEnd:
        this.softDropping = false;
        return;
      case Action.Start:
        return;
      default:
        break;
    }

    // Everything below needs a live piece; during the line-clear pause there
    // is none, and inputs are simply dropped.
    if (!this.active || this.clearAnim) return;

    switch (action) {
      case Action.MoveLeft:
        this.tryMove(-1, 0);
        return;
      case Action.MoveRight:
        this.tryMove(1, 0);
        return;
      case Action.RotateCW:
        this.tryRotate(1);
        return;
      case Action.RotateCCW:
        this.tryRotate(-1);
        return;
      case Action.Rotate180:
        this.tryRotate(2);
        return;
      case Action.HardDrop:
        this.hardDrop();
        return;
      case Action.Hold:
        this.holdPiece();
        return;
      default:
        return;
    }
  }

  // -------------------------------------------------------------- advancement

  /**
   * Advance the simulation by `deltaMs` of wall time. This is the ONLY way time
   * enters the core. Large deltas (tab was backgrounded) are clamped so a
   * single frame cannot dump a piece through the floor.
   */
  step(deltaMs: number): void {
    if (this.phase !== Phase.Playing) return;
    const dt = Math.min(Math.max(0, deltaMs), TUNING.maxStepMs);
    if (dt === 0) return;
    this.stats.elapsedMs += dt;

    if (this.clearAnim) {
      this.clearAnim.elapsedMs += dt;
      if (this.clearAnim.elapsedMs >= this.clearAnim.durationMs) {
        const rows = this.clearAnim.rows;
        this.clearAnim = null;
        this.board.clearRows(rows);
        this.spawnNext();
      }
      return;
    }

    if (!this.active) return;

    this.applyGravity(dt);
    this.updateGrounded();

    if (this.grounded && this.active) {
      this.lockTimerMs += dt;
      if (this.lockTimerMs >= TUNING.lockDelayMs) this.lockPiece(false);
    }
  }

  /**
   * Move the piece down `rows` rows right now, ignoring elapsed time. Returns
   * how many rows it actually fell. Locking is left to `step`, so a rule that
   * calls this cannot accidentally skip lock delay.
   */
  stepGravity(rows: number = 1): number {
    if (this.phase !== Phase.Playing || !this.active || this.clearAnim) return 0;
    let moved = 0;
    for (let i = 0; i < rows; i++) {
      if (!this.fallOneRow()) break;
      moved++;
    }
    this.gravityAcc = 0;
    this.updateGrounded();
    return moved;
  }

  private applyGravity(dt: number): void {
    const gravity = gravityIntervalMs(this.level);
    const interval = this.softDropping
      ? Math.max(TUNING.softDropMinIntervalMs, gravity / TUNING.softDropFactor)
      : gravity;

    this.gravityAcc += dt;
    // Bound the loop: at extreme levels the interval can be sub-millisecond.
    let budget = TUNING.board.height;
    while (this.gravityAcc >= interval && budget-- > 0) {
      this.gravityAcc -= interval;
      if (!this.fallOneRow()) {
        this.gravityAcc = 0;
        break;
      }
      if (this.softDropping) this.score += TUNING.scoring.softDropPerCell;
    }
  }

  /** One row of free fall. Does NOT reset lock delay — gravity is not a move. */
  private fallOneRow(): boolean {
    if (!this.active) return false;
    const next = cellsAt(this.active.type, this.active.rotation, this.active.x, this.active.y + 1);
    if (this.board.collides(next)) return false;
    this.active.y += 1;
    this.lastActionWasRotation = false;
    return true;
  }

  // ------------------------------------------------------------------ actions

  private tryMove(dx: number, dy: number): boolean {
    if (!this.active) return false;
    const next = cellsAt(
      this.active.type,
      this.active.rotation,
      this.active.x + dx,
      this.active.y + dy,
    );
    if (this.board.collides(next)) return false;
    this.active.x += dx;
    this.active.y += dy;
    this.lastActionWasRotation = false;
    this.onSuccessfulAction();
    return true;
  }

  private tryRotate(quarterTurns: number): boolean {
    if (!this.active) return false;
    const from = this.active.rotation;
    const to: Rotation = rotateIndex(from, quarterTurns);
    const kicks = getKicks(this.active.type, from, to);

    for (let i = 0; i < kicks.length; i++) {
      const kick = kicks[i];
      const x = this.active.x + kick.x;
      const y = this.active.y + kick.y;
      if (this.board.collides(cellsAt(this.active.type, to, x, y))) continue;

      this.active.x = x;
      this.active.y = y;
      this.active.rotation = to;
      this.lastActionWasRotation = true;
      this.lastKickIndex = i;
      this.onSuccessfulAction();
      return true;
    }
    return false;
  }

  /** Shared lock-delay bookkeeping for player-driven moves and rotations. */
  private onSuccessfulAction(): void {
    const wasGrounded = this.grounded;
    this.updateGrounded();
    if ((wasGrounded || this.lockTimerMs > 0) && this.lockResets < TUNING.maxLockResets) {
      this.lockTimerMs = 0;
      this.lockResets++;
    }
    if (!this.grounded && this.lockResets < TUNING.maxLockResets) {
      // Airborne again: the timer only runs on the ground.
      this.lockTimerMs = 0;
    }
  }

  private hardDrop(): void {
    if (!this.active) return;
    const distance = this.board.dropDistance(pieceCells(this.active));
    if (distance > 0) {
      this.active.y += distance;
      this.score += distance * TUNING.scoring.hardDropPerCell;
      this.lastActionWasRotation = false;
    }
    this.lockPiece(true, Math.max(0, distance));
  }

  private holdPiece(): void {
    if (!this.active || !this.canHold) return;
    const outgoing = this.active.type;
    const incoming = this.hold ?? this.generator.next();
    this.hold = outgoing;
    this.canHold = false;
    this.placePiece(spawnPiece(incoming));
    this.events.emit('onHold', { held: outgoing, swappedIn: incoming });
  }

  // -------------------------------------------------------------------- locks

  private lockPiece(hardDrop: boolean, dropDistance = 0): void {
    if (!this.active) return;
    const piece = this.active;
    const cells = pieceCells(piece);
    const tspin = this.detectTSpin(piece);

    this.board.lock(cells, piece.type);
    this.active = null;
    this.grounded = false;
    this.lockTimerMs = 0;
    this.lockResets = 0;
    this.stats.piecesPlaced++;

    const fullRows = this.board.findFullRows();
    const lineCount = fullRows.length;

    this.events.emit('onLock', { piece, cells, tspin, lines: lineCount, hardDrop, dropDistance });

    const topples = this.rules === RuleSet.Balance && this.balanceAfterClears(fullRows).risk > 1;

    this.combo = lineCount > 0 ? this.combo + 1 : -1;
    if (this.combo > this.stats.maxCombo) this.stats.maxCombo = this.combo;

    const result = scoreClear(
      { lines: lineCount, tspin, level: this.level, combo: this.combo, backToBack: this.backToBack },
      TUNING.scoring,
    );

    if (lineCount > 0 || tspin !== 'none') this.score += result.total;

    if (lineCount > 0) {
      this.backToBack = result.difficult;
      this.recordClear(lineCount, tspin);

      const previousLevel = this.level;
      this.lines += lineCount;
      this.level = Math.max(this.startLevel, levelForLines(this.lines, TUNING.linesPerLevel));

      this.events.emit('onLineClear', {
        rows: fullRows,
        lines: lineCount,
        tspin,
        backToBack: result.backToBackApplied,
        combo: this.combo,
        points: result.total,
      });

      if (this.level > previousLevel) {
        this.events.emit('onLevelUp', { level: this.level, previousLevel });
      }

      if (topples) {
        this.board.clearRows(fullRows);
        this.gameOver('topple');
        return;
      }

      // Rows stay on the board for the duration of the clear animation; `step`
      // removes them and spawns the next piece when the timer expires.
      this.clearAnim = {
        rows: fullRows,
        elapsedMs: 0,
        durationMs: TUNING.lineClearDurationMs,
      };
      return;
    }

    if (topples) {
      this.gameOver('topple');
      return;
    }

    this.spawnNext();
  }

  private recordClear(lineCount: number, tspin: TSpinKind): void {
    if (tspin !== 'none') this.stats.tspins++;
    if (lineCount === 1) this.stats.singles++;
    else if (lineCount === 2) this.stats.doubles++;
    else if (lineCount === 3) this.stats.triples++;
    else if (lineCount === 4) this.stats.tetrises++;
  }

  /**
   * Three-corner rule. The last successful action must have been a rotation and
   * at least three of the four cells diagonally around the T's centre must be
   * blocked. It is a Mini unless both "front" corners (the two beside the T's
   * stem) are blocked — or the rotation needed the final kick, which always
   * promotes to a full T-spin.
   */
  private detectTSpin(piece: ActivePiece): TSpinKind {
    if (piece.type !== 'T' || !this.lastActionWasRotation) return 'none';

    const c = tCenter(piece);
    const corners = {
      tl: this.board.isSolid(c.x - 1, c.y - 1),
      tr: this.board.isSolid(c.x + 1, c.y - 1),
      bl: this.board.isSolid(c.x - 1, c.y + 1),
      br: this.board.isSolid(c.x + 1, c.y + 1),
    };
    const filled = Number(corners.tl) + Number(corners.tr) + Number(corners.bl) + Number(corners.br);
    if (filled < 3) return 'none';

    // Front corners follow the direction the stem points.
    const front: Record<Rotation, [boolean, boolean]> = {
      0: [corners.tl, corners.tr],
      1: [corners.tr, corners.br],
      2: [corners.br, corners.bl],
      3: [corners.bl, corners.tl],
    };
    const [f1, f2] = front[piece.rotation];
    if (f1 && f2) return 'full';
    // The last kick in the table is the "wide" one; a spin that needed it is
    // scored as a full T-spin.
    return this.lastKickIndex === 4 ? 'full' : 'mini';
  }

  // ------------------------------------------------------------------- spawns

  private spawnNext(): void {
    this.canHold = true;
    this.placePiece(spawnPiece(this.generator.next()));
  }

  /** Install a piece at spawn and check block-out. */
  private placePiece(piece: ActivePiece): void {
    this.active = piece;
    this.gravityAcc = 0;
    this.lockTimerMs = 0;
    this.lockResets = 0;
    this.lastActionWasRotation = false;
    this.lastKickIndex = 0;

    if (this.rules === RuleSet.Balance) {
      // BALANCE has no ceiling loss. Feed a blocked piece in above the stack;
      // the hidden buffer normally makes this adjustment small.
      while (this.board.collides(pieceCells(piece))) piece.y--;
    } else if (this.board.collides(pieceCells(piece))) {
      this.active = piece; // keep it visible in the overlap for the render layer
      this.gameOver('blockout');
      return;
    }
    this.updateGrounded();
  }

  private updateGrounded(): void {
    if (!this.active) {
      this.grounded = false;
      return;
    }
    const below = cellsAt(
      this.active.type,
      this.active.rotation,
      this.active.x,
      this.active.y + 1,
    );
    const nowGrounded = this.board.collides(below);
    if (nowGrounded && !this.grounded && this.lockResets >= TUNING.maxLockResets) {
      // Reset budget spent: the piece locks on this contact, no more grace.
      this.lockTimerMs = TUNING.lockDelayMs;
    }
    this.grounded = nowGrounded;
  }

  private balanceAfterClears(rows: readonly number[]): BalanceState {
    if (rows.length === 0) return this.balanceState();
    const board = this.board.clone();
    board.clearRows(rows);
    const cells = board.snapshot();
    let mass = 0;
    let moment = 0;
    for (let y = 0; y < board.height; y++) {
      for (let x = 0; x < board.width; x++) {
        if (cells[y * board.width + x] === null) continue;
        mass++;
        moment += x + 0.5;
      }
    }
    const pivotX = board.width / 2;
    const centerX = mass === 0 ? pivotX : moment / mass;
    const offset = centerX - pivotX;
    const limit = Math.max(
      TUNING.balance.minimumLimit,
      TUNING.balance.emptyLimit - mass * TUNING.balance.limitLossPerCell,
    );
    return { mass, centerX, offset, limit, risk: Math.abs(offset) / limit };
  }

  private gameOver(reason: GameOverEvent['reason']): void {
    this.softDropping = false;
    this.clearAnim = null;
    this.gameOverReason = reason;
    this.setPhase(Phase.GameOver);
    this.events.emit('onGameOver', {
      reason,
      score: this.score,
      lines: this.lines,
      level: this.level,
    });
  }
}

function emptyStats(): GameStats {
  return {
    piecesPlaced: 0,
    singles: 0,
    doubles: 0,
    triples: 0,
    tetrises: 0,
    tspins: 0,
    maxCombo: 0,
    elapsedMs: 0,
  };
}
